import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import {
  useWebsiteStudentConnectSubmissions,
  useUpdateWebsiteStudentConnectReadStatus,
  useUpdateWebsiteStudentConnectSubmission,
  type WebsiteStudentConnectSubmission,
} from '../hooks/useWebsiteStudentConnectSubmissions';
import {
  STUDENT_HELP_TYPES,
  useWebsiteStudentHelpRequests,
  useUpdateWebsiteStudentHelpRequestReadStatus,
  useUpdateWebsiteStudentHelpRequest,
  type WebsiteStudentHelpRequest,
} from '../hooks/useWebsiteStudentHelpRequests';

type AdminTab = 'connect' | 'help';

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const HelperText = styled.p`
  color: var(--onSurfaceVariant);
  margin: 0.5rem 0 1rem;
  font-size: 0.9rem;
`;

const TabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
  margin-bottom: 1.25rem;
`;

const TabButton = styled.button<{ $active?: boolean }>`
  border: 1px solid
    ${({ $active }) =>
      $active
        ? 'color-mix(in srgb, var(--primary) 45%, var(--outline))'
        : 'color-mix(in srgb, var(--outline) 80%, transparent)'};
  border-radius: 999px;
  padding: 0.45rem 0.85rem;
  background: ${({ $active }) =>
    $active
      ? 'color-mix(in srgb, var(--primary) 12%, var(--surface))'
      : 'color-mix(in srgb, var(--surface) 85%, var(--surfaceVariant))'};
  color: ${({ $active }) => ($active ? 'var(--onSurface)' : 'var(--onSurfaceVariant)')};
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
`;

const TableCard = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  overflow: hidden;
  margin-bottom: 1.5rem;
`;

const TableWrap = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  th,
  td {
    padding: 0.75rem 1rem;
    border-bottom: 1px solid var(--outline);
    text-align: left;
    font-size: 0.9rem;
    color: var(--onSurfaceVariant);
  }
  th {
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--onSurfaceVariant);
    background: var(--surfaceVariant);
  }
`;

const ClickableRow = styled.tr`
  cursor: pointer;
  &:hover {
    background: var(--surfaceVariant);
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 40;
  padding: 1.5rem;
`;

const ModalCard = styled.div`
  background: var(--surface);
  border-radius: 16px;
  width: min(700px, 100%);
  max-height: min(90vh, 900px);
  overflow: auto;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.2);
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid var(--outline);
  position: sticky;
  top: 0;
  background: var(--surface);
  z-index: 1;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.1rem;
  color: var(--onSurface);
`;

const ModalCloseButton = styled.button`
  border: none;
  background: transparent;
  color: var(--onSurfaceVariant);
  font-size: 1.15rem;
  cursor: pointer;
`;

const ModalBody = styled.div`
  padding: 1rem 1.25rem 1.25rem;
  display: grid;
  gap: 0.8rem;
`;

const DetailGroup = styled.div`
  display: grid;
  gap: 0.25rem;
`;

const DetailLabel = styled.label`
  margin: 0;
  color: var(--onSurfaceVariant);
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
`;

const DetailValue = styled.p`
  margin: 0;
  color: var(--onSurface);
  font-size: 0.95rem;
`;

const FieldInput = styled.input`
  width: 100%;
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.55rem 0.7rem;
  font: inherit;
  font-size: 0.92rem;
  box-sizing: border-box;
`;

const FieldSelect = styled.select`
  width: 100%;
  border: 1px solid var(--outline);
  border-radius: 10px;
  background: var(--surface);
  color: var(--onSurface);
  padding: 0.55rem 0.7rem;
  font: inherit;
  font-size: 0.92rem;
  box-sizing: border-box;
`;

const StatusPill = styled.span<{ $isRead: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 9999px;
  padding: 0.15rem 0.5rem;
  font-size: 0.72rem;
  font-weight: 600;
  background: ${({ $isRead }) => ($isRead ? 'var(--surfaceVariant)' : 'var(--primary)')};
  color: ${({ $isRead }) => ($isRead ? 'var(--onSurfaceVariant)' : 'var(--onPrimary)')};
`;

const StatusButton = styled.button`
  border: 1px solid var(--outline);
  background: var(--surface);
  color: var(--onSurface);
  border-radius: 9999px;
  padding: 0.45rem 0.8rem;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button`
  border: none;
  background: var(--primary);
  color: var(--onPrimary);
  border-radius: 9999px;
  padding: 0.55rem 1rem;
  font-size: 0.88rem;
  font-weight: 700;
  cursor: pointer;
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.6rem;
  align-items: center;
`;

const FeedbackText = styled.p<{ $tone?: 'success' | 'error' }>`
  margin: 0;
  font-size: 0.88rem;
  color: ${({ $tone }) =>
    $tone === 'error' ? 'var(--error)' : $tone === 'success' ? 'var(--primary)' : 'var(--onSurfaceVariant)'};
`;

export const StudentsAdminPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<AdminTab>('connect');

  const {
    data: connectData,
    isLoading: connectLoading,
    error: connectError,
  } = useWebsiteStudentConnectSubmissions(activeTab === 'connect');
  const {
    data: helpData,
    isLoading: helpLoading,
    error: helpError,
  } = useWebsiteStudentHelpRequests(activeTab === 'help');

  const updateConnectRead = useUpdateWebsiteStudentConnectReadStatus();
  const updateConnect = useUpdateWebsiteStudentConnectSubmission();
  const updateHelpRead = useUpdateWebsiteStudentHelpRequestReadStatus();
  const updateHelp = useUpdateWebsiteStudentHelpRequest();

  const [selectedConnect, setSelectedConnect] = useState<WebsiteStudentConnectSubmission | null>(
    null
  );
  const [connectDraft, setConnectDraft] = useState<WebsiteStudentConnectSubmission | null>(null);
  const [connectFeedback, setConnectFeedback] = useState<string | null>(null);

  const [selectedHelp, setSelectedHelp] = useState<WebsiteStudentHelpRequest | null>(null);
  const [helpDraft, setHelpDraft] = useState<WebsiteStudentHelpRequest | null>(null);
  const [helpFeedback, setHelpFeedback] = useState<string | null>(null);

  const openConnect = (row: WebsiteStudentConnectSubmission) => {
    setSelectedConnect(row);
    setConnectDraft(row);
    setConnectFeedback(null);
    if (!row.is_read) {
      updateConnectRead.mutate({ id: row.id, isRead: true });
      const next = { ...row, is_read: true, read_at: new Date().toISOString() };
      setSelectedConnect(next);
      setConnectDraft(next);
    }
  };

  const openHelp = (row: WebsiteStudentHelpRequest) => {
    setSelectedHelp(row);
    setHelpDraft(row);
    setHelpFeedback(null);
    if (!row.is_read) {
      updateHelpRead.mutate({ id: row.id, isRead: true });
      const next = { ...row, is_read: true, read_at: new Date().toISOString() };
      setSelectedHelp(next);
      setHelpDraft(next);
    }
  };

  useEffect(() => {
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setSelectedConnect(null);
        setConnectDraft(null);
        setSelectedHelp(null);
        setHelpDraft(null);
      }
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, []);

  const saveConnect = async () => {
    if (!connectDraft) return;
    setConnectFeedback(null);
    if (
      !connectDraft.name.trim() ||
      !connectDraft.phone.trim() ||
      !connectDraft.email.trim() ||
      !connectDraft.parent_name.trim() ||
      !connectDraft.parent_contact_info.trim() ||
      !connectDraft.school.trim() ||
      !connectDraft.grade.trim()
    ) {
      setConnectFeedback('All fields are required.');
      return;
    }
    try {
      await updateConnect.mutateAsync({
        id: connectDraft.id,
        updates: {
          name: connectDraft.name,
          phone: connectDraft.phone,
          email: connectDraft.email,
          parent_name: connectDraft.parent_name,
          parent_contact_info: connectDraft.parent_contact_info,
          school: connectDraft.school,
          grade: connectDraft.grade,
        },
      });
      setSelectedConnect(connectDraft);
      setConnectFeedback('Saved.');
    } catch {
      setConnectFeedback('Could not save changes. Please try again.');
    }
  };

  const saveHelp = async () => {
    if (!helpDraft) return;
    setHelpFeedback(null);
    if (
      !helpDraft.help_type.trim() ||
      !helpDraft.contact_info.trim() ||
      !helpDraft.grade.trim() ||
      !helpDraft.subject.trim()
    ) {
      setHelpFeedback('All fields are required.');
      return;
    }
    try {
      await updateHelp.mutateAsync({
        id: helpDraft.id,
        updates: {
          help_type: helpDraft.help_type,
          contact_info: helpDraft.contact_info,
          grade: helpDraft.grade,
          subject: helpDraft.subject,
        },
      });
      setSelectedHelp(helpDraft);
      setHelpFeedback('Saved.');
    } catch {
      setHelpFeedback('Could not save changes. Please try again.');
    }
  };

  return (
    <Page>
      <HeaderTitle>Students</HeaderTitle>
      <HelperText>
        Connect form and Request for Help submissions from the public website Students page.
      </HelperText>

      <TabBar role="tablist" aria-label="Students admin sections">
        <TabButton
          type="button"
          role="tab"
          $active={activeTab === 'connect'}
          aria-selected={activeTab === 'connect'}
          onClick={() => setActiveTab('connect')}
        >
          Connect Form
        </TabButton>
        <TabButton
          type="button"
          role="tab"
          $active={activeTab === 'help'}
          aria-selected={activeTab === 'help'}
          onClick={() => setActiveTab('help')}
        >
          Request for help
        </TabButton>
      </TabBar>

      {activeTab === 'connect' && (
        <>
          {connectLoading && <HelperText>Loading…</HelperText>}
          {connectError && (
            <HelperText style={{ color: 'var(--error)' }}>
              Could not load connect submissions. You may need to run the students migration.
            </HelperText>
          )}
          {!connectLoading && (connectData?.length ?? 0) > 0 && (
            <TableCard>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>School</th>
                      <th>Grade</th>
                      <th>Status</th>
                      <th>Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {connectData?.map((row) => (
                      <ClickableRow
                        key={row.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => openConnect(row)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openConnect(row);
                          }
                        }}
                      >
                        <td>{row.name}</td>
                        <td>{row.email}</td>
                        <td>{row.phone}</td>
                        <td>{row.school}</td>
                        <td>{row.grade}</td>
                        <td>
                          <StatusPill $isRead={row.is_read}>
                            {row.is_read ? 'Read' : 'Unread'}
                          </StatusPill>
                        </td>
                        <td>
                          {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                        </td>
                      </ClickableRow>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </TableCard>
          )}
          {!connectLoading && !connectError && (connectData?.length ?? 0) === 0 && (
            <HelperText>No Connect Form submissions yet.</HelperText>
          )}
        </>
      )}

      {activeTab === 'help' && (
        <>
          {helpLoading && <HelperText>Loading…</HelperText>}
          {helpError && (
            <HelperText style={{ color: 'var(--error)' }}>
              Could not load help requests. You may need to run the students migration.
            </HelperText>
          )}
          {!helpLoading && (helpData?.length ?? 0) > 0 && (
            <TableCard>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>Help type</th>
                      <th>Contact info</th>
                      <th>Grade</th>
                      <th>Subject</th>
                      <th>Status</th>
                      <th>Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {helpData?.map((row) => (
                      <ClickableRow
                        key={row.id}
                        role="button"
                        tabIndex={0}
                        onClick={() => openHelp(row)}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openHelp(row);
                          }
                        }}
                      >
                        <td>{row.help_type}</td>
                        <td>{row.contact_info}</td>
                        <td>{row.grade}</td>
                        <td>{row.subject}</td>
                        <td>
                          <StatusPill $isRead={row.is_read}>
                            {row.is_read ? 'Read' : 'Unread'}
                          </StatusPill>
                        </td>
                        <td>
                          {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                        </td>
                      </ClickableRow>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
            </TableCard>
          )}
          {!helpLoading && !helpError && (helpData?.length ?? 0) === 0 && (
            <HelperText>No Request for Help submissions yet.</HelperText>
          )}
        </>
      )}

      {selectedConnect && connectDraft && (
        <ModalOverlay
          onClick={() => {
            setSelectedConnect(null);
            setConnectDraft(null);
          }}
        >
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Connect Form Details</ModalTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <StatusButton
                  type="button"
                  onClick={async () => {
                    const nextIsRead = !connectDraft.is_read;
                    await updateConnectRead.mutateAsync({
                      id: connectDraft.id,
                      isRead: nextIsRead,
                    });
                    const next = {
                      ...connectDraft,
                      is_read: nextIsRead,
                      read_at: nextIsRead ? new Date().toISOString() : null,
                    };
                    setConnectDraft(next);
                    setSelectedConnect(next);
                  }}
                  disabled={updateConnectRead.isPending}
                >
                  {connectDraft.is_read ? 'Mark as unread' : 'Mark as read'}
                </StatusButton>
                <ModalCloseButton
                  type="button"
                  aria-label="Close connect details"
                  onClick={() => {
                    setSelectedConnect(null);
                    setConnectDraft(null);
                  }}
                >
                  ×
                </ModalCloseButton>
              </div>
            </ModalHeader>
            <ModalBody>
              {(
                [
                  ['Name', 'name'],
                  ['Phone', 'phone'],
                  ['Email', 'email'],
                  ['Parent Name', 'parent_name'],
                  ['Parent Contact info', 'parent_contact_info'],
                  ['School', 'school'],
                  ['Grade', 'grade'],
                ] as const
              ).map(([label, key]) => (
                <DetailGroup key={key}>
                  <DetailLabel htmlFor={`connect-${key}`}>{label}</DetailLabel>
                  <FieldInput
                    id={`connect-${key}`}
                    value={connectDraft[key]}
                    onChange={(event) =>
                      setConnectDraft((prev) =>
                        prev ? { ...prev, [key]: event.target.value } : prev
                      )
                    }
                  />
                </DetailGroup>
              ))}
              <DetailGroup>
                <DetailLabel>Submitted</DetailLabel>
                <DetailValue>
                  {connectDraft.created_at
                    ? new Date(connectDraft.created_at).toLocaleString()
                    : '—'}
                </DetailValue>
              </DetailGroup>
              <ActionRow>
                <PrimaryButton
                  type="button"
                  onClick={() => void saveConnect()}
                  disabled={updateConnect.isPending}
                >
                  {updateConnect.isPending ? 'Saving…' : 'Save changes'}
                </PrimaryButton>
                {connectFeedback && (
                  <FeedbackText $tone={connectFeedback === 'Saved.' ? 'success' : 'error'}>
                    {connectFeedback}
                  </FeedbackText>
                )}
              </ActionRow>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}

      {selectedHelp && helpDraft && (
        <ModalOverlay
          onClick={() => {
            setSelectedHelp(null);
            setHelpDraft(null);
          }}
        >
          <ModalCard onClick={(event) => event.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Help Request Details</ModalTitle>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <StatusButton
                  type="button"
                  onClick={async () => {
                    const nextIsRead = !helpDraft.is_read;
                    await updateHelpRead.mutateAsync({
                      id: helpDraft.id,
                      isRead: nextIsRead,
                    });
                    const next = {
                      ...helpDraft,
                      is_read: nextIsRead,
                      read_at: nextIsRead ? new Date().toISOString() : null,
                    };
                    setHelpDraft(next);
                    setSelectedHelp(next);
                  }}
                  disabled={updateHelpRead.isPending}
                >
                  {helpDraft.is_read ? 'Mark as unread' : 'Mark as read'}
                </StatusButton>
                <ModalCloseButton
                  type="button"
                  aria-label="Close help request details"
                  onClick={() => {
                    setSelectedHelp(null);
                    setHelpDraft(null);
                  }}
                >
                  ×
                </ModalCloseButton>
              </div>
            </ModalHeader>
            <ModalBody>
              <DetailGroup>
                <DetailLabel htmlFor="help-type">Help type</DetailLabel>
                <FieldSelect
                  id="help-type"
                  value={helpDraft.help_type}
                  onChange={(event) =>
                    setHelpDraft((prev) =>
                      prev ? { ...prev, help_type: event.target.value } : prev
                    )
                  }
                >
                  {!STUDENT_HELP_TYPES.includes(
                    helpDraft.help_type as (typeof STUDENT_HELP_TYPES)[number]
                  ) && helpDraft.help_type ? (
                    <option value={helpDraft.help_type}>{helpDraft.help_type}</option>
                  ) : null}
                  {STUDENT_HELP_TYPES.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </FieldSelect>
              </DetailGroup>
              <DetailGroup>
                <DetailLabel htmlFor="help-contact">Contact info</DetailLabel>
                <FieldInput
                  id="help-contact"
                  value={helpDraft.contact_info}
                  onChange={(event) =>
                    setHelpDraft((prev) =>
                      prev ? { ...prev, contact_info: event.target.value } : prev
                    )
                  }
                />
              </DetailGroup>
              <DetailGroup>
                <DetailLabel htmlFor="help-grade">Grade</DetailLabel>
                <FieldInput
                  id="help-grade"
                  value={helpDraft.grade}
                  onChange={(event) =>
                    setHelpDraft((prev) => (prev ? { ...prev, grade: event.target.value } : prev))
                  }
                />
              </DetailGroup>
              <DetailGroup>
                <DetailLabel htmlFor="help-subject">Subject</DetailLabel>
                <FieldInput
                  id="help-subject"
                  value={helpDraft.subject}
                  onChange={(event) =>
                    setHelpDraft((prev) =>
                      prev ? { ...prev, subject: event.target.value } : prev
                    )
                  }
                />
              </DetailGroup>
              <DetailGroup>
                <DetailLabel>Submitted</DetailLabel>
                <DetailValue>
                  {helpDraft.created_at ? new Date(helpDraft.created_at).toLocaleString() : '—'}
                </DetailValue>
              </DetailGroup>
              <ActionRow>
                <PrimaryButton
                  type="button"
                  onClick={() => void saveHelp()}
                  disabled={updateHelp.isPending}
                >
                  {updateHelp.isPending ? 'Saving…' : 'Save changes'}
                </PrimaryButton>
                {helpFeedback && (
                  <FeedbackText $tone={helpFeedback === 'Saved.' ? 'success' : 'error'}>
                    {helpFeedback}
                  </FeedbackText>
                )}
              </ActionRow>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      )}
    </Page>
  );
};
